import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LogOut, History, LayoutDashboard, Inbox, UserRound, PenTool } from "lucide-react";

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const navLinkClass = (active) =>
    `text-xs uppercase tracking-[0.18em] px-3 py-2 border-b-2 transition-colors duration-200 ${
      active
        ? "border-[#ED5A24] text-[#0A0A0B]"
        : "border-transparent text-[#52525B] hover:text-[#0A0A0B]"
    }`;

  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-black/5"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="brand-link"
          className="flex items-center gap-3 group"
        >
          <img
            src="/peskypixel-logo.png"
            alt="PeskyPixel — vectorising made easy"
            className="h-10 sm:h-12 w-auto transition-transform duration-300 group-hover:scale-105"
          />
        </Link>

        <nav className="flex items-center gap-1 sm:gap-2">
          {user ? (
            <>
              <Link to="/dashboard" data-testid="nav-dashboard" aria-label="Dashboard" className={navLinkClass(location.pathname === "/dashboard")}>
                <span className="inline-flex items-center gap-2"><LayoutDashboard className="w-3.5 h-3.5" /><span className="hidden sm:inline">Dashboard</span></span>
              </Link>
              <Link to="/sketch" data-testid="nav-sketch" aria-label="Sketch to logo" className={navLinkClass(location.pathname === "/sketch")}>
                <span className="inline-flex items-center gap-2"><PenTool className="w-3.5 h-3.5" /><span className="hidden sm:inline">Sketch</span></span>
              </Link>
              <Link to="/history" data-testid="nav-history" aria-label="History" className={navLinkClass(location.pathname === "/history")}>
                <span className="inline-flex items-center gap-2"><History className="w-3.5 h-3.5" /><span className="hidden sm:inline">History</span></span>
              </Link>
              <Link to="/account" data-testid="nav-account" aria-label="Account" className={navLinkClass(location.pathname === "/account")}>
                <span className="inline-flex items-center gap-2"><UserRound className="w-3.5 h-3.5" /><span className="hidden sm:inline">Account</span></span>
              </Link>
              {user.role === "admin" && (
                <Link to="/enquiries" data-testid="nav-enquiries" aria-label="Enquiries" className={navLinkClass(location.pathname === "/enquiries")}>
                  <span className="inline-flex items-center gap-2"><Inbox className="w-3.5 h-3.5" /><span className="hidden sm:inline">Enquiries</span></span>
                </Link>
              )}
              <div className="hidden md:flex items-center gap-2 ml-2 pl-4 border-l border-black/10">
                <span className="text-xs text-[#52525B]" data-testid="user-email">{user.email}</span>
              </div>
              <Button
                data-testid="logout-btn"
                aria-label="Logout"
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="rounded-none hover:bg-[#0A0A0B] hover:text-white text-xs uppercase tracking-[0.18em]"
              >
                <LogOut className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Logout</span>
              </Button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className={navLinkClass(location.pathname === "/login")}>
                Login
              </Link>
              <Link to="/register" data-testid="nav-register">
                <Button
                  size="sm"
                  className="rounded-none bg-gradient-to-r from-[#ED5A24] to-[#1F8FED] hover:from-[#C44619] hover:to-[#0F6FBF] text-white text-xs uppercase tracking-[0.18em] font-bold px-5"
                >
                  Get Started
                </Button>
              </Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}
